/**
 * PlanningView.tsx
 * Vue plein écran optimisée pour affichage TV/grands écrans
 * Affiche 2 semaines côte à côte avec rafraîchissement automatique
 * Lit la semaine de départ depuis l'URL (?week=YYYY-MM-DD)
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { WAG_COLORS, stringToColor } from '../hooks/useSchedulerData';
import { Loader2, RefreshCw, Calendar, AlertCircle, GraduationCap, Maximize2, Minimize2 } from 'lucide-react';

// Types
type CompanyType = 'oger' | 'wag';

interface Employee {
  id: string;
  full_name: string;
  role: string;
  is_active: boolean;
  company?: CompanyType; // 'oger' par défaut si null/undefined
}


interface Project {
  id: string;
  code: string;
  name: string;
  color: string;
  category: string;
}

interface DiversSubProject {
  id: string;
  parent_project_id: string;
  divers_number: string;
  name: string;
}

interface Assignment {
  id: string;
  employee_id: string;
  project_id: string;
  divers_sub_project_id?: string | null;
  assignment_date: string;
  period: 'Q1' | 'Q2' | 'Q3' | 'Q4' | 'AM' | 'PM' | 'DAY';
}

interface Leave {
  id: string;
  employee_id: string;
  start_date: string;
  end_date: string;
  type: string;
}

interface TrainingDay {
  employee_id: string;
  day: string;
  description: string;
}

// Helper pour formater la date en YYYY-MM-DD local
const formatDateLocal = (d: Date): string => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper pour parser une date YYYY-MM-DD
const parseDate = (str: string): Date => {
  const [year, month, day] = str.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Helper pour obtenir le lundi d'une semaine
const getMonday = (d: Date): Date => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
};

// Helper pour ajouter des jours
const addDays = (d: Date, days: number): Date => {
  const result = new Date(d);
  result.setDate(result.getDate() + days);
  return result;
};

// Helper pour obtenir le numéro de semaine ISO
const getWeekNumber = (d: Date): number => {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

// Helper pour obtenir les jours ouvrés d'une semaine (Lun-Ven)
const getWeekDays = (monday: Date): Date[] => {
  const days: Date[] = [];
  for (let i = 0; i < 5; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    days.push(d);
  }
  return days;
};

export default function PlanningView() {
  const [searchParams] = useSearchParams();

  // États
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [diversSubProjects, setDiversSubProjects] = useState<DiversSubProject[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [trainingDays, setTrainingDays] = useState<TrainingDay[]>([]);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Calculer les semaines à partir du paramètre URL ou aujourd'hui
  const week1Monday = useMemo(() => {
    const weekParam = searchParams.get('week');
    if (weekParam) {
      try {
        const parsedDate = parseDate(weekParam);
        return getMonday(parsedDate);
      } catch {
        return getMonday(new Date());
      }
    }
    return getMonday(new Date());
  }, [searchParams]);

  const week2Monday = useMemo(() => addDays(week1Monday, 7), [week1Monday]);
  const week1Days = useMemo(() => getWeekDays(week1Monday), [week1Monday]);
  const week2Days = useMemo(() => getWeekDays(week2Monday), [week2Monday]);
  const week1Number = getWeekNumber(week1Monday);
  const week2Number = getWeekNumber(week2Monday);

  // Chargement des données
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Calculer la plage de dates (2 semaines complètes)
      const startDate = formatDateLocal(week1Days[0]);
      const endDate = formatDateLocal(addDays(week2Days[4], 2)); // Inclure le weekend

      // Charger en parallèle
      const [
        { data: empData },
        { data: projData },
        { data: diversData },
        { data: assignData },
        { data: leaveData },
        { data: trainingData }
      ] = await Promise.all([
        supabase.from('employees').select('id, full_name, role, is_active, company').eq('is_active', true).not('role', 'in', '(secretariat,admin)').order('full_name'),
        supabase.from('projects').select('*').eq('is_active', true),
        supabase.from('divers_sub_projects').select('*').eq('is_active', true),
        supabase.from('project_assignments')
          .select('*')
          .gte('assignment_date', startDate)
          .lte('assignment_date', endDate),
        supabase.from('leaves')
          .select('*')
          .eq('status', 'validated')
          .lte('start_date', endDate)
          .gte('end_date', startDate),
        supabase.from('training_days')
          .select('*')
          .gte('day', startDate)
          .lte('day', endDate)
      ]);

      // Ajouter les couleurs aux projets
      const projectsWithColors = (projData || []).map(p => ({
        ...p,
        color: stringToColor(p.id)
      }));

      setEmployees(empData || []);
      setProjects(projectsWithColors);
      setDiversSubProjects(diversData || []);
      setAssignments(assignData || []);
      setLeaves(leaveData || []);
      setTrainingDays(trainingData || []);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  }, [week1Days, week2Days]);

  // Chargement initial et auto-refresh
  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 120000); // 2 minutes
    return () => clearInterval(interval);
  }, [loadData]);

  // Gestion plein écran
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Fonction pour trier les employés selon l'ordre sauvegardé pour une semaine
  const sortEmployeesByWeek = useCallback((emps: Employee[], weekNum: number, year: number) => {
    const weekKey = `employee_display_order_${year}_S${weekNum}`;
    const savedOrder = localStorage.getItem(weekKey);
    if (savedOrder) {
      try {
        const orderIds: string[] = JSON.parse(savedOrder);
        return [...emps].sort((a, b) => {
          const indexA = orderIds.indexOf(a.id);
          const indexB = orderIds.indexOf(b.id);
          if (indexA === -1 && indexB === -1) return a.full_name.localeCompare(b.full_name);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });
      } catch {
        return emps;
      }
    }
    return emps;
  }, []);

  // Filtrer les employés (exclure certains rôles)
  const filteredEmployees = useMemo(() => {
    return employees.filter(e => !['admin', 'secretariat'].includes(e.role));
  }, [employees]);

  // Employés triés pour chaque semaine (ordre indépendant)
  const week1Employees = useMemo(() => {
    return sortEmployeesByWeek(filteredEmployees, week1Number, week1Monday.getFullYear());
  }, [filteredEmployees, week1Number, week1Monday, sortEmployeesByWeek]);

  const week2Employees = useMemo(() => {
    return sortEmployeesByWeek(filteredEmployees, week2Number, week2Monday.getFullYear());
  }, [filteredEmployees, week2Number, week2Monday, sortEmployeesByWeek]);

  // Rendu d'une cellule
  const renderCell = (emp: Employee, day: Date) => {
    const dateStr = formatDateLocal(day);
    const isWeekend = day.getDay() === 0 || day.getDay() === 6;

    const leaveInfo = leaves.find(l =>
      l.employee_id === emp.id && dateStr >= l.start_date && dateStr <= l.end_date
    );
    const training = trainingDays.find(t =>
      t.employee_id === emp.id && t.day === dateStr
    );

    // Récupérer les assignations (même logique que AdminScheduler - uniquement Q1/Q2/Q3/Q4)
    const q1 = assignments.find(a => a.employee_id === emp.id && a.assignment_date === dateStr && a.period === 'Q1');
    const q2 = assignments.find(a => a.employee_id === emp.id && a.assignment_date === dateStr && a.period === 'Q2');
    const q3 = assignments.find(a => a.employee_id === emp.id && a.assignment_date === dateStr && a.period === 'Q3');
    const q4 = assignments.find(a => a.employee_id === emp.id && a.assignment_date === dateStr && a.period === 'Q4');

    const p1 = q1 ? projects.find(p => p.id === q1.project_id) : null;
    const p2 = q2 ? projects.find(p => p.id === q2.project_id) : null;
    const p3 = q3 ? projects.find(p => p.id === q3.project_id) : null;
    const p4 = q4 ? projects.find(p => p.id === q4.project_id) : null;

    // Formation
    if (training) {
      return (
        <td key={dateStr} style={{
          background: '#f3e8ff',
          borderBottom: '1px solid #e2e8f0',
          borderRight: '1px solid #e2e8f0',
          textAlign: 'center',
          color: '#7e22ce',
          verticalAlign: 'middle',
          height: '100px'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
            <GraduationCap size={24} />
            <span style={{ fontSize: '0.8rem', fontWeight: '700' }}>FORMATION</span>
          </div>
        </td>
      );
    }

    // Congé / Maladie (style uniforme gris comme AdminScheduler)
    if (leaveInfo) {
      const LEAVE_LABELS: Record<string, string> = {
        cp: 'CP', rtt: 'RTT', maladie: 'MALADIE', sans_solde: 'SANS SOLDE', formation: 'FORMATION', autre: 'AUTRE'
      };
      return (
        <td key={dateStr} style={{
          background: '#f1f5f9',
          borderBottom: '1px solid #e2e8f0',
          borderRight: '1px solid #e2e8f0',
          textAlign: 'center',
          color: '#94a3b8',
          verticalAlign: 'middle',
          height: '100px'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', opacity: 0.7 }}>
            <AlertCircle size={20} />
            <span style={{ fontSize: '0.7rem', fontWeight: '700' }}>
              {(LEAVE_LABELS[leaveInfo.type] || leaveInfo.type).toUpperCase().substring(0, 8)}
            </span>
          </div>
        </td>
      );
    }

    // Cellule normale
    const isFullDay = p1 && p2 && p3 && p4 && p1.id === p2.id && p2.id === p3.id && p3.id === p4.id;
    const isAmSame = p1 && p2 && p1.id === p2.id;
    const isPmSame = p3 && p4 && p3.id === p4.id;

    const renderBlock = (proj: typeof p1, assignment: typeof q1, isMerged: boolean) => {
      if (!proj) {
        // Cellule vide : fond transparent (pas de gris)
        return (
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} />
        );
      }

      let displayName = proj.name || '';
      let displayCode = proj.code || '';
      if (assignment?.divers_sub_project_id) {
        const subProj = diversSubProjects.find(sp => sp.id === assignment.divers_sub_project_id);
        if (subProj) {
          displayName = subProj.name;
          displayCode = subProj.divers_number;
        }
      }

      return (
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: `${proj.color}26`,
          padding: '4px',
          overflow: 'hidden'
        }}>
          <div style={{
            fontWeight: '800',
            fontSize: isMerged ? '0.9rem' : '0.7rem',
            color: proj.color,
            textTransform: 'uppercase',
            textAlign: 'center',
            lineHeight: 1.2
          }}>
            {displayName}
          </div>
          {isMerged && displayCode && (
            <div style={{ fontSize: '0.6rem', opacity: 0.7, color: proj.color, marginTop: '2px' }}>
              {displayCode}
            </div>
          )}
        </div>
      );
    };

    // Logique identique à AdminScheduler : afficher toujours matin/après-midi
    return (
      <td key={dateStr} style={{
        padding: 0,
        height: '100px',
        borderBottom: '1px solid #e2e8f0',
        borderRight: '1px solid #e2e8f0',
        background: isWeekend ? '#f9fafb' : 'white'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {isFullDay ? (
            <div style={{ flex: 1, display: 'flex' }}>
              {renderBlock(p1, q1, true)}
            </div>
          ) : (
            <>
              {/* MATIN */}
              <div style={{ flex: 1, display: 'flex', borderBottom: '1px dashed #e2e8f0' }}>
                {isAmSame ? (
                  renderBlock(p1, q1, true)
                ) : (
                  <>
                    {renderBlock(p1, q1, false)}
                    {renderBlock(p2, q2, false)}
                  </>
                )}
              </div>
              {/* APRES-MIDI */}
              <div style={{ flex: 1, display: 'flex' }}>
                {isPmSame ? (
                  renderBlock(p3, q3, true)
                ) : (
                  <>
                    {renderBlock(p3, q3, false)}
                    {renderBlock(p4, q4, false)}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </td>
    );
  };

  // Rendu d'une semaine (avec liste d'employés spécifique à cette semaine)
  const renderWeek = (weekDays: Date[], weekNumber: number, color: string, bgColor: string, weekEmployees: Employee[]) => (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {/* En-tête semaine */}
      <div style={{
        padding: '12px 20px',
        background: bgColor,
        borderBottom: `3px solid ${color}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Calendar size={24} color={color} />
          <span style={{ fontWeight: '800', fontSize: '1.2rem', color }}>
            Semaine {weekNumber}
          </span>
        </div>
        <span style={{ color: '#64748b', fontWeight: '500', fontSize: '0.9rem' }}>
          {weekDays[0].toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} - {weekDays[4].toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
        </span>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', background: 'white' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
            <tr>
              <th style={{
                padding: '10px 12px',
                textAlign: 'left',
                minWidth: '160px',
                background: 'white',
                borderBottom: '2px solid #e2e8f0',
                borderRight: '1px solid #e2e8f0',
                color: '#64748b',
                fontSize: '0.75rem',
                fontWeight: '700'
              }}>
                COLLABORATEUR
              </th>
              {weekDays.map((day, idx) => (
                <th key={idx} style={{
                  padding: '8px',
                  textAlign: 'center',
                  minWidth: '120px',
                  background: '#f8fafc',
                  borderBottom: '2px solid #e2e8f0',
                  borderRight: '1px solid #e2e8f0'
                }}>
                  <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', fontWeight: '600' }}>
                    {day.toLocaleDateString('fr-FR', { weekday: 'long' })}
                  </div>
                  <div style={{ fontSize: '1.2rem', fontWeight: '800', color: '#1e293b' }}>
                    {day.getDate()}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weekEmployees.map((emp) => (
              <tr key={emp.id}>
                <td style={{
                  padding: '8px 12px',
                  background: emp.company === 'wag' ? WAG_COLORS.bg : 'white',
                  borderBottom: '1px solid #f1f5f9',
                  borderRight: '1px solid #f1f5f9',
                  borderLeft: emp.company === 'wag' ? `3px solid ${WAG_COLORS.border}` : 'none',
                  fontWeight: '700',
                  fontSize: '0.85rem',
                  color: emp.company === 'wag' ? WAG_COLORS.text : '#1e293b',
                  whiteSpace: 'nowrap'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {/* Badge WAG */}
                    {emp.company === 'wag' && (
                      <span style={{
                        fontSize: '0.6rem',
                        background: WAG_COLORS.border,
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontWeight: '700',
                        flexShrink: 0
                      }}>
                        WAG
                      </span>
                    )}
                    {emp.full_name || '?'}
                  </div>
                </td>
                {weekDays.map((day) => renderCell(emp, day))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Chargement
  if (loading && employees.length === 0) {
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8fafc',
        color: '#64748b',
        gap: '16px'
      }}>
        <Loader2 size={48} style={{ animation: 'spin 1s linear infinite' }} />
        <p style={{ fontSize: '1.2rem', fontWeight: '500' }}>Chargement du planning...</p>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      background: '#f8fafc'
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 24px',
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h1 style={{ margin: 0, color: 'white', fontSize: '1.3rem', fontWeight: '700' }}>
            Planning 2 Semaines
          </h1>
          <span style={{
            background: 'rgba(255,255,255,0.15)',
            color: 'white',
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '0.8rem',
            fontWeight: '500'
          }}>
            S{week1Number} & S{week2Number} • {filteredEmployees.length} collaborateurs
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Indicateur de dernière mise à jour */}
          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>
            Mis à jour : {lastRefresh.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </span>

          {/* Bouton refresh */}
          <button
            onClick={loadData}
            disabled={loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '8px',
              color: 'white',
              cursor: loading ? 'wait' : 'pointer',
              fontSize: '0.85rem',
              fontWeight: '500'
            }}
          >
            <RefreshCw size={16} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            Actualiser
          </button>

          {/* Bouton plein écran */}
          <button
            onClick={toggleFullscreen}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              background: isFullscreen ? '#10b981' : 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: '500'
            }}
          >
            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            {isFullscreen ? 'Quitter' : 'Plein écran'}
          </button>
        </div>
      </div>

      {/* Contenu - 2 semaines côte à côte */}
      <div style={{
        flex: 1,
        display: 'flex',
        gap: '2px',
        background: '#e2e8f0',
        overflow: 'hidden'
      }}>
        {renderWeek(week1Days, week1Number, '#2563eb', '#eff6ff', week1Employees)}
        {renderWeek(week2Days, week2Number, '#7c3aed', '#f3e8ff', week2Employees)}
      </div>

      {/* Footer */}
      <div style={{
        padding: '8px 24px',
        background: 'white',
        borderTop: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: '0.75rem',
        color: '#64748b',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981', animation: 'pulse 2s infinite' }} />
          <span>Actualisation automatique toutes les 2 minutes</span>
        </div>
        <div style={{ display: 'flex', gap: '20px' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: 14, height: 14, borderRadius: '4px', background: WAG_COLORS.bg, border: `2px solid ${WAG_COLORS.border}` }} /> WAG
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: 14, height: 14, borderRadius: '4px', background: '#f1f5f9', border: '1px solid #cbd5e1' }} /> Congé / Absent
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: 14, height: 14, borderRadius: '4px', background: '#f3e8ff' }} /> Formation
          </span>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
